// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { UsMaTFunction } from "~@jii/translation";
import { palette } from "~design-system";

import documentTextIcon from "./Guide/icons/documentText.svg";
import homeIcon from "./Guide/icons/home.svg";
import housingIllustration from "./Home/ResourceCategoryCta/housingIllustration.svg";
import {
  CompletedQuestionnaireCardCopy,
  GuideTopicConfig,
  QuestionnaireCardCopy,
  ResourceCategoryConfig,
} from "./types";

export const GUIDE_TOPICS: GuideTopicConfig[] = [
  {
    slug: "id-documents",
    iconSrc: documentTextIcon,
    label: (t) => t(($) => $.reentry.overview.guideCta.idDocuments),
  },
  {
    slug: "housing",
    iconSrc: homeIcon,
    label: (t) => t(($) => $.reentry.overview.guideCta.housing),
  },
];

const HOUSING_GRADIENT_START = "#6F97AB";

/**
 * Add a new entry here to add a new resource category card to the Overview
 * page - each renders its own illustration + heading + description + CTA.
 */
export const RESOURCE_CATEGORIES: ResourceCategoryConfig[] = [
  {
    category: "housing",
    illustrationSrc: housingIllustration,
    illustrationBackground: `linear-gradient(135deg, ${HOUSING_GRADIENT_START}, ${palette.slate})`,
    copy: (t) => ({
      heading: t(($) => $.reentry.overview.resourceCategories.housing.heading),
      description: t(
        ($) => $.reentry.overview.resourceCategories.housing.description,
      ),
      cta: t(($) => $.reentry.overview.resourceCategories.housing.cta),
    }),
  },
];

export const QUESTIONNAIRE_STATUS_COPY = {
  NOT_STARTED: (t: UsMaTFunction): QuestionnaireCardCopy => ({
    heading: t(($) => $.reentry.overview.questionnaireCta.notStarted.heading),
    description: t(
      ($) => $.reentry.overview.questionnaireCta.notStarted.description,
    ),
    cta: t(($) => $.reentry.overview.questionnaireCta.notStarted.cta),
  }),
  IN_PROGRESS: (t: UsMaTFunction): QuestionnaireCardCopy => ({
    heading: t(($) => $.reentry.overview.questionnaireCta.inProgress.heading),
    description: t(
      ($) => $.reentry.overview.questionnaireCta.inProgress.description,
    ),
    cta: t(($) => $.reentry.overview.questionnaireCta.inProgress.cta),
  }),
  COMPLETED: (t: UsMaTFunction): CompletedQuestionnaireCardCopy => ({
    heading: t(($) => $.reentry.overview.questionnaireCta.completed.heading),
    recommendedTaskCount: (count) =>
      t(
        ($) =>
          $.reentry.overview.questionnaireCta.completed.recommendedTaskCount,
        { count },
      ),
    cta: t(($) => $.reentry.overview.questionnaireCta.completed.cta),
    reviewProfile: {
      heading: t(
        ($) => $.reentry.overview.questionnaireCta.reviewProfile.heading,
      ),
      description: t(
        ($) => $.reentry.overview.questionnaireCta.reviewProfile.description,
      ),
      cta: t(($) => $.reentry.overview.questionnaireCta.reviewProfile.cta),
    },
  }),
};
